import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { identifyUser, resetUser, trackEvent } from "@/lib/posthog";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ needsEmailConfirmation: boolean }>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const PENDING_SIGNUP_KEY = "ip_pending_signup";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        identifyUser(session.user.id, { email: session.user.email });
        if (event === "SIGNED_IN") {
          // If we marked a pending signup, this SIGNED_IN is the completion.
          let pending: { method?: string } | null = null;
          try {
            const raw = localStorage.getItem(PENDING_SIGNUP_KEY);
            if (raw) pending = JSON.parse(raw);
          } catch { /* ignore */ }
          if (pending) {
            localStorage.removeItem(PENDING_SIGNUP_KEY);
            trackEvent("signup_completed", { method: pending.method ?? "email" });
            // Fire-and-forget internal owner notification (server-side guarded
            // against duplicates and non-new accounts)
            supabase.functions.invoke("notify-new-signup").catch(() => { /* non-critical */ });
          } else {
            trackEvent("user_signed_in");
          }
        }
      } else if (event === "SIGNED_OUT") {
        trackEvent("user_signed_out");
        resetUser();
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) identifyUser(session.user.id, { email: session.user.email });
    });

    return () => subscription.unsubscribe();
  }, []);

  const markPendingSignup = (method: string) => {
    try {
      localStorage.setItem(PENDING_SIGNUP_KEY, JSON.stringify({ method, t: Date.now() }));
    } catch { /* ignore */ }
  };

  const signUp = async (email: string, password: string, displayName?: string): Promise<{ needsEmailConfirmation: boolean }> => {
    trackEvent("signup_started", { method: "email" });
    markPendingSignup("email");
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: { display_name: displayName },
      },
    });
    if (error) {
      trackEvent("signup_failed", { method: "email", code: error.status, message: error.message });
      trackEvent("auth_error", { stage: "signup", message: error.message });
      try { localStorage.removeItem(PENDING_SIGNUP_KEY); } catch { /* noop */ }
      throw error;
    }
    // Supabase can silently "succeed" when the email is already registered
    // (returns a user with no identities and no session). Surface that so the
    // UI can guide the person to sign in instead of failing later.
    if (!data.session && data.user && (data.user.identities?.length ?? 0) === 0) {
      try { localStorage.removeItem(PENDING_SIGNUP_KEY); } catch { /* noop */ }
      throw new Error("User already registered");
    }
    trackEvent("signup_submitted", { method: "email" });
    // If Supabase requires email confirmation, no session is returned.
    return { needsEmailConfirmation: !data.session };
  };

  const signIn = async (email: string, password: string) => {
    trackEvent("login_started", { method: "email" });
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      trackEvent("login_failed", { method: "email", message: error.message });
      trackEvent("auth_error", { stage: "login", message: error.message });
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    trackEvent("signup_started", { method: "google" });
    trackEvent("login_started", { method: "google" });
    markPendingSignup("google");
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}/dashboard`,
    });
    if (result?.error) {
      trackEvent("auth_error", { stage: "oauth", provider: "google", message: String(result.error?.message ?? result.error) });
      try { localStorage.removeItem(PENDING_SIGNUP_KEY); } catch { /* noop */ }
      throw result.error instanceof Error ? result.error : new Error(String(result.error));
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signInWithGoogle, signOut, resetPassword, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
